// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import {
  Box,
  Chip,
  Select,
  Checkbox,
  MenuItem,
  TextField,
  InputLabel,
  SelectProps,
  FormControl,
  OutlinedInput,
  TextFieldProps,
  FormHelperText,
  Autocomplete,
  Typography,
  createFilterOptions,
} from '@mui/material';

// ----------------------------------------------------------------------

type RHFSelectProps = TextFieldProps & {
  name: string;
  native?: boolean;
  maxHeight?: boolean | number;
  children: React.ReactNode;
};

export function RHFSelect({
  name,
  native,
  maxHeight = 220,
  helperText,
  children,
  value,
  defaultValue,
  ...other
}: RHFSelectProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          select
          fullWidth
          SelectProps={{
            native,
            MenuProps: {
              PaperProps: {
                sx: {
                  ...(!native && {
                    px: 1,
                    maxHeight: typeof maxHeight === 'number' ? maxHeight : 'unset',
                    '& .MuiMenuItem-root': {
                      px: 1,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    },
                  }),
                },
              },
            },
            sx: { textTransform: 'capitalize' },
          }}
          error={!!error}
          helperText={error ? error?.message : helperText}
          sx={{
            '& .MuiInputBase-root.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#9e9e9e',
              cursor: 'not-allowed',
            },
            '& .MuiInputLabel-root.Mui-disabled': {
              color: '#9e9e9e',
            },
            '& .MuiSelect-icon.Mui-disabled': {
              color: '#bdbdbd',
            },
            ...other.sx,
          }}
          {...other}
        >
          {children}
        </TextField>
      )}
    />
  );
}
interface SelectOption {
  value: string | number | object;
  label: string;
}

type RHFSearchableSelectProps = Omit<TextFieldProps, 'select' | 'children'> & {
  name: string;
  options: SelectOption[];
  maxHeight?: boolean | number;
};

export function RHFSearchableSelect({
  name,
  options,
  maxHeight = 220,
  helperText,
  placeholder,
  ...other
}: RHFSearchableSelectProps) {
  const { control } = useFormContext();

  const filter = createFilterOptions<SelectOption>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selectedOption =
          options.find((option) => {
            if (typeof option.value === 'object' && typeof field.value === 'object') {
              return JSON.stringify(option.value) === JSON.stringify(field.value);
            }
            return option.value === field.value;
          }) || null;

        return (
          <Autocomplete
            options={options}
            value={selectedOption}
            onChange={(_, newValue) => {
              field.onChange(newValue?.value || '');
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              if (typeof option.value === 'object' && typeof value.value === 'object') {
                return JSON.stringify(option.value) === JSON.stringify(value.value);
              }
              return option.value === value.value;
            }}
            filterOptions={(options, params) => {
              return filter(options, params);
            }}
            noOptionsText="No options found"
            renderInput={(params) => (
              <TextField
                {...params}
                {...other}
                error={!!error}
                helperText={error ? error.message : helperText}
                placeholder={placeholder || 'Search and select...'}
                sx={{
                  ...other.sx,
                  '& .MuiInputBase-input': {
                    textTransform: 'capitalize',
                  },
                }}
              />
            )}
            renderOption={(props, option, index) => (
              <li
                {...props}
                key={`${
                  typeof option.value === 'object' ? JSON.stringify(option.value) : option.value
                }-${option.label}-${index}`}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  margin: '0 8px',
                  fontSize: '0.875rem',
                  textTransform: 'capitalize',
                }}
              >
                {option.label}
              </li>
            )}
            ListboxProps={{
              style: {
                maxHeight: typeof maxHeight === 'number' ? maxHeight : 'unset',
                padding: '8px 0',
              },
            }}
            sx={{
              '& .MuiAutocomplete-listbox': {
                '& .MuiAutocomplete-option': {
                  px: 1,
                  borderRadius: 0.75,
                  typography: 'body2',
                  textTransform: 'capitalize',
                },
              },
            }}
          />
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMultiSelectProps = SelectProps & {
  name: string;
  label?: string;
  chip?: boolean;
  checkbox?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  options: {
    label: string;
    value: string | number;
    onClick?: () => void;
  }[];
};

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  sx,
  ...other
}: RHFMultiSelectProps) {
  const { control } = useFormContext();

  const renderValues = (selectedIds: string[]) => {
    // @ts-ignore
    const selectedItems = options.filter((item) => selectedIds.includes(item.value));

    if (!selectedItems.length && placeholder) {
      return (
        <Box component="em" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      );
    }

    if (chip) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedItems.map((item) => (
            <Chip key={`${item.label}-${item.value}`} size="small" label={item.label} />
          ))}
        </Box>
      );
    }

    return selectedItems.map((item) => item.label).join(', ');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={sx}>
          {label && <InputLabel id={name}> {label} </InputLabel>}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            // @ts-ignore
            renderValue={renderValues}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
            }}
            sx={{
              '& .MuiInputBase-root.Mui-disabled': {
                backgroundColor: '#f5f5f5',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#9e9e9e',
                cursor: 'not-allowed',
              },
              '& .MuiInputLabel-root.Mui-disabled': {
                color: '#9e9e9e',
              },
              '& .MuiSelect-icon.Mui-disabled': {
                color: '#bdbdbd',
              },
            }}
            {...other}
          >
            {placeholder && (
              <MenuItem
                disabled
                value=""
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                <em> {placeholder} </em>
              </MenuItem>
            )}

            {options.map((option) => {
              const selected = field.value.includes(option.value);

              return (
                <MenuItem
                  key={`${option.value}-${option.label}`}
                  value={option.value}
                  onClick={() => option.onClick && option?.onClick()}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 0.75,
                    typography: 'body2',
                    ...(selected && {
                      fontWeight: 'fontWeightMedium',
                    }),
                    ...(checkbox && {
                      p: 0.25,
                    }),
                  }}
                >
                  {checkbox && <Checkbox disableRipple size="small" checked={selected} />}

                  {option.label}
                </MenuItem>
              );
            })}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

// Types
type RGS = string;

interface ProviderRgs {
  rgsId: RGS;
  providerId: string;
  providerName: string;
}

interface UserProviderRgs {
  rgsId: RGS;
  providerIds: string[];
}

type RHFSearchableMultiSelectProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  name: string;
  providers: ProviderRgs[];
  maxHeight?: boolean | number;
  disabled?: boolean;
};

export function RHFSearchableMultiSelect({
  name,
  providers,
  maxHeight = 220,
  helperText,
  disabled,
  ...other
}: RHFSearchableMultiSelectProps) {
  const { control } = useFormContext();

  const convertToProviders = (userProviderRgs: UserProviderRgs[]): ProviderRgs[] => {
    const result: ProviderRgs[] = [];

    userProviderRgs.forEach((userProvider) => {
      userProvider.providerIds.forEach((providerId) => {
        const originalProvider = providers.find(
          (p) => p.rgsId === userProvider.rgsId && p.providerId === providerId
        );
        if (originalProvider) {
          result.push(originalProvider);
        }
      });
    });

    return result;
  };

  const convertToUserProviderRgs = (selectedProviders: ProviderRgs[]): UserProviderRgs[] => {
    const grouped: { [rgsId: string]: string[] } = {};

    selectedProviders.forEach((provider) => {
      if (!grouped[provider.rgsId]) {
        grouped[provider.rgsId] = [];
      }
      if (!grouped[provider.rgsId].includes(provider.providerId)) {
        grouped[provider.rgsId].push(provider.providerId);
      }
    });

    return Object.entries(grouped).map(([rgsId, providerIds]) => ({
      rgsId,
      providerIds,
    }));
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selectedProviders = convertToProviders(field.value || []);

        return (
          <Autocomplete
            multiple
            disabled={disabled}
            options={providers}
            value={selectedProviders}
            onChange={(_, newValue) => {
              const userProviderRgs = convertToUserProviderRgs(newValue);
              field.onChange(userProviderRgs);
            }}
            // Keep dropdown open when selecting items
            disableCloseOnSelect
            filterOptions={(options, { inputValue }) => {
              return options.filter((option) =>
                option.providerName.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
            getOptionLabel={(option) => `${option.providerName}(${option.rgsId})`}
            isOptionEqualToValue={(option, value) =>
              option.rgsId === value.rgsId && option.providerId === value.providerId
            }
            renderInput={(params) => (
              <TextField
                {...params}
                {...other}
                error={!!error}
                helperText={error ? error.message : helperText}
                placeholder="Search and select providers..."
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option.rgsId}-${option.providerId}`}
                  label={`${option.providerName}(${option.rgsId})`}
                  size="small"
                  variant="outlined"
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <Box
                component="li"
                {...props}
                key={`${option.rgsId}-${option.providerId}`}
                sx={{
                  backgroundColor: selected ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: selected ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Typography variant="body2">
                  {option.providerName}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({option.rgsId})
                  </Typography>
                </Typography>
              </Box>
            )}
            ListboxProps={{
              style: {
                maxHeight: typeof maxHeight === 'number' ? maxHeight : 'unset',
              },
            }}
            sx={{
              '& .MuiAutocomplete-tag': {
                margin: 0.5,
              },
            }}
          />
        );
      }}
    />
  );
}
